// ---------------------------------------------------------------------------
// applyWaitingUpdate — what the update toast's "Reload" actually does.
// Its own module (not registerSW.ts) so it can be tested: registerSW imports
// the plugin's virtual module, which only exists inside a Vite build.
// ---------------------------------------------------------------------------

/** The "Reload" button on the update toast (UpdateReadyNotice).
 *
 *  THE FIRST-VISIT BUG (PLE-13, Patrick Heagany Sep 24: the button "did
 *  nothing"). The plugin's updateSW(true) only reloads when `controllerchange`
 *  fires on a page that was ALREADY controlled when it loaded (workbox-window's
 *  `controlling` event, isUpdate). A first visit is never controlled — the
 *  worker installed during it, and clientsClaim:false (vite.config.ts)
 *  deliberately leaves open pages alone. So the event never fires, nothing
 *  reloads, and the toast stays.
 *
 *  Reproduced Sep 30 in headless Chrome against a local production build
 *  (first visit → new sw.js → toast → click): old code, no reload and the
 *  toast stuck; this code, one reload and the page controlled by the new
 *  worker.
 *
 *  Three cases:
 *  - Controlled page (a returning visit): the plugin's path works; keep it.
 *  - Uncontrolled page, nothing waiting — THE COMMON FIRST-VISIT CASE,
 *    measured: a worker only waits while the old one controls some page, and
 *    this page is controlled by nothing, so the new worker is already active.
 *    Only the reload was missing.
 *  - Uncontrolled page with a worker still waiting: another, older tab is
 *    held by the old worker. Activate it, and reload only once it reports
 *    ACTIVATED — earlier, the old worker serves the old build and the toast
 *    comes straight back. If activation never reports, reload anyway and say
 *    so: a dead button is the bug being fixed. */
export async function applyWaitingUpdate(opts: {
  registration: ServiceWorkerRegistration | null;
  updateSW: (reload?: boolean) => Promise<void>;
  reload?: () => void;
  timeoutMs?: number;
}): Promise<void> {
  const { registration, updateSW, reload = () => window.location.reload(), timeoutMs = 4000 } = opts;
  const controlled = typeof navigator !== "undefined" && !!navigator.serviceWorker?.controller;
  if (controlled) {
    await updateSW(true);
    return;
  }
  const waiting = registration?.waiting ?? null;
  if (!waiting) {
    console.info("[update] uncontrolled page, new worker already active — reloading");
    reload();
    return;
  }
  console.info("[update] uncontrolled page, new worker waiting (another tab holds the old one) — activating, then reloading");
  await new Promise<void>((resolve) => {
    const timer = setTimeout(() => {
      console.warn(`[update] new worker not activated after ${timeoutMs}ms — reloading anyway (state=${waiting.state})`);
      resolve();
    }, timeoutMs);
    // Listen BEFORE asking, or a fast activation is missed.
    waiting.addEventListener("statechange", () => {
      if (waiting.state === "activated") {
        clearTimeout(timer);
        resolve();
      }
    });
    waiting.postMessage({ type: "SKIP_WAITING" });
  });
  reload();
}
